import React, { useEffect, useState } from 'react';
import { format, parseISO, subDays } from 'date-fns';
import { ArrowPathIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../api/apiConfig';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';
import MetricChart from './MetricChart';

// Interface for mental clarity data from the API
interface MentalClarityEntry {
  id: number;
  level: 1 | 2 | 3; // Mental clarity level: 1 (Foggy), 2 (Clear), 3 (Sharp)
  comment?: string | null;
  created_at: string; // ISO date string
}

// Interface for the data format Recharts expects
interface ChartData {
  date: string; // Formatted date for XAxis
  level: number;
  comment?: string | null;
  fullDate: string; // For tooltip
  originalDate: Date; // For sorting
}

const mentalClarityLabels: { [key: number]: string } = {
  1: 'Foggy',
  2: 'Clear',
  3: 'Sharp',
};

const CustomTooltip: React.FC<any> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload as ChartData;
    return (
      <div
        style={{
          padding: SPACING[8],
          backgroundColor: COLORS.bg.panel,
          border: `1px solid ${COLORS.border.hover}`,
          borderRadius: RADIUS,
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        }}
      >
        <p
          style={{
            fontSize: TYPOGRAPHY.size.sm,
            fontWeight: TYPOGRAPHY.weight.semibold,
            color: COLORS.text.primary,
          }}
        >
          {`Date: ${data.fullDate}`}
        </p>
        <p
          style={{
            fontSize: TYPOGRAPHY.size.sm,
            color: COLORS.text.secondary,
          }}
        >
          {`Mental Clarity: ${mentalClarityLabels[data.level] || data.level}`}
        </p>
        {data.comment && (
          <p
            style={{
              marginTop: SPACING[4],
              fontSize: TYPOGRAPHY.size.xs,
              color: COLORS.text.tertiary,
              fontStyle: 'italic',
            }}
          >
            {`Comment: ${data.comment}`}
          </p>
        )}
      </div>
    );
  }
  return null;
};

const MentalClarityTracker: React.FC = () => {
  const [mentalClarityData, setMentalClarityData] = useState<ChartData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMentalClarityData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/api/mental-clarity/');

      if (response.status >= 200 && response.status < 300) {
        const data: MentalClarityEntry[] = response.data;

        const mappedData: (ChartData | null)[] = data.map(entry => {
          if (!entry || typeof entry.created_at !== 'string' || entry.created_at.trim() === '') {
            console.warn('Skipping mental clarity entry due to missing or invalid created_at:', entry);
            return null;
          }
          try {
            const parsedDate = parseISO(entry.created_at);
            if (isNaN(parsedDate.getTime())) {
              console.warn('Skipping mental clarity entry due to unparseable created_at string:', entry.created_at, entry);
              return null;
            }
            return {
              date: format(parsedDate, 'MMM d'),
              level: entry.level,
              comment: entry.comment,
              fullDate: format(parsedDate, 'MMM d, yyyy'),
              originalDate: parsedDate,
            };
          } catch (dateProcessingError) {
            console.error('Error processing date for mental clarity entry:', entry.created_at, entry, dateProcessingError);
            return null;
          }
        });

        // Filter out nulls using a type guard
        const filteredData: ChartData[] = mappedData.filter(
          (item): item is ChartData => item !== null
        );

        // Sort data by date in ascending order (oldest to newest)
        filteredData.sort((a, b) => {
          const dateA = a.originalDate.getTime();
          const dateB = b.originalDate.getTime();
          return dateA - dateB;
        });

        // Filter to show only last 30 days
        const thirtyDaysAgo = subDays(new Date(), 30);
        const last30DaysData = filteredData.filter(item => item.originalDate >= thirtyDaysAgo);

        setMentalClarityData(last30DaysData);
      } else {
        throw new Error(`Failed to fetch mental clarity data: ${response.status}`);
      }
    } catch (err) {
      console.error('Error fetching mental clarity data:', err);
      setError('Failed to load mental clarity data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMentalClarityData();

    // Listen for mental clarity refresh events
    const handleMentalClarityRefresh = () => {
      fetchMentalClarityData();
    };

    window.addEventListener('mentalClarityDataUpdated', handleMentalClarityRefresh);

    return () => {
      window.removeEventListener('mentalClarityDataUpdated', handleMentalClarityRefresh);
    };
  }, []);

  const cardStyle: React.CSSProperties = {
    backgroundColor: COLORS.bg.panel,
    border: `1px solid ${COLORS.border.default}`,
    borderRadius: RADIUS,
    height: '320px',
  };

  const centeredContentStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const iconStyle: React.CSSProperties = {
    width: '32px',
    height: '32px',
    marginBottom: SPACING[8],
  };

  if (loading) {
    return (
      <div style={{ ...cardStyle, ...centeredContentStyle }}>
        <ArrowPathIcon
          style={{
            ...iconStyle,
            color: COLORS.text.tertiary,
            animation: 'spin 1s linear infinite',
          }}
        />
        <p
          style={{
            fontSize: TYPOGRAPHY.size.sm,
            color: COLORS.text.tertiary,
          }}
        >
          Loading mental clarity data...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...cardStyle, ...centeredContentStyle, padding: SPACING[16] }}>
        <ExclamationCircleIcon
          style={{
            ...iconStyle,
            color: COLORS.status.error,
          }}
        />
        <p
          style={{
            fontSize: TYPOGRAPHY.size.sm,
            color: COLORS.status.error,
            textAlign: 'center',
          }}
        >
          {error}
        </p>
      </div>
    );
  }

  if (mentalClarityData.length === 0) {
    return (
      <div style={{ ...cardStyle, ...centeredContentStyle }}>
        <p
          style={{
            fontSize: TYPOGRAPHY.size.sm,
            color: COLORS.text.tertiary,
          }}
        >
          No mental clarity data recorded yet.
        </p>
        <p
          style={{
            fontSize: TYPOGRAPHY.size.xs,
            color: COLORS.text.tertiary,
            marginTop: SPACING[4],
          }}
        >
          Start tracking your mental clarity using the Quick Add menu!
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        ...cardStyle,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <h3
        style={{
          fontSize: TYPOGRAPHY.headings.sizes.h2,
          fontFamily: TYPOGRAPHY.headings.fontFamily,
          fontWeight: TYPOGRAPHY.weight.medium,
          color: COLORS.text.primary,
          marginBottom: SPACING[16],
          paddingLeft: SPACING[16],
          paddingRight: SPACING[16],
          paddingTop: SPACING[16],
        }}
      >
        Mental Clarity Evolution (Last 30 days)
      </h3>
      <div style={{ flexGrow: 1, paddingLeft: SPACING[8], paddingRight: SPACING[8] }}>
        <MetricChart
          data={mentalClarityData}
          series={[{ dataKey: 'level', name: 'Mental Clarity', metricType: 'wellness', color: COLORS.status.info }]}
          height={200}
          showLegend={false}
          customTooltip={CustomTooltip}
          yDomain={[1, 3]}
          yTicks={[1, 2, 3]}
          yTickFormatter={(v) => ({ 1: 'Foggy', 2: 'Clear', 3: 'Sharp' }[v] || '')}
        />
      </div>
    </div>
  );
};

export default MentalClarityTracker;
